import { useConnection } from "@solana/wallet-adapter-react";
import { Connection } from "@solana/web3.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { RPC_FALLBACK, RPC_HISTORY } from "../lib/constants";
import {
  loadRouterMetrics,
  mostCompleteMetrics,
  PLATFORM_METRICS_BASELINE,
  type PlatformMetrics,
} from "../lib/metrics";

const CACHE_KEY = "tribe-trader-platform-metrics-v1";
let sharedLoad:
  | {
      key: string;
      promise: Promise<{
        best?: PlatformMetrics;
        error?: unknown;
      }>;
    }
  | undefined;

function readCache(): PlatformMetrics {
  try {
    const value = JSON.parse(window.localStorage.getItem(CACHE_KEY) || "null") as
      | Record<string, string | number>
      | null;
    if (!value) return PLATFORM_METRICS_BASELINE;
    const cached = {
      traders: Number(value.traders),
      trades: Number(value.trades),
      buys: Number(value.buys),
      sells: Number(value.sells),
      volumeLamports: BigInt(value.volumeLamports),
      tribeFeeLamports: BigInt(value.tribeFeeLamports),
      interfaceFeeLamports: BigInt(value.interfaceFeeLamports),
    };
    return (
      mostCompleteMetrics([PLATFORM_METRICS_BASELINE, cached]) ||
      PLATFORM_METRICS_BASELINE
    );
  } catch {
    return PLATFORM_METRICS_BASELINE;
  }
}

function writeCache(metrics: PlatformMetrics): void {
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        ...metrics,
        volumeLamports: metrics.volumeLamports.toString(),
        tribeFeeLamports: metrics.tribeFeeLamports.toString(),
        interfaceFeeLamports: metrics.interfaceFeeLamports.toString(),
      }),
    );
  } catch {
    // Metrics remain available in memory when storage is blocked or full.
  }
}

function readConnections(connection: Connection): Connection[] {
  const endpoints = [connection.rpcEndpoint, RPC_FALLBACK, RPC_HISTORY].filter(
    (endpoint, index, values): endpoint is string =>
      Boolean(endpoint) && values.indexOf(endpoint) === index,
  );
  return endpoints.map((endpoint) =>
    endpoint === connection.rpcEndpoint
      ? connection
      : new Connection(endpoint, "confirmed"),
  );
}

function loadBestMetrics(connection: Connection) {
  const connections = readConnections(connection);
  const key = connections.map((item) => item.rpcEndpoint).join("|");
  if (sharedLoad?.key === key) return sharedLoad.promise;
  const promise = (async () => {
    const results = await Promise.allSettled(
      connections.map((readConnection) => loadRouterMetrics(readConnection)),
    );
    const best = mostCompleteMetrics(
      results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      ),
    );
    const rejected = results.find((result) => result.status === "rejected");
    return {
      best,
      error: rejected?.status === "rejected" ? rejected.reason : undefined,
    };
  })();
  sharedLoad = { key, promise };
  void promise.finally(() => {
    if (sharedLoad?.promise === promise) sharedLoad = undefined;
  });
  return promise;
}

export function usePlatformMetrics() {
  const { connection } = useConnection();
  const [metrics, setMetrics] = useState(readCache);
  const metricsRef = useRef(metrics);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const inFlight = useRef<Promise<void> | undefined>(undefined);

  const refresh = useCallback((): Promise<void> => {
    if (inFlight.current) return inFlight.current;
    const task = (async () => {
      setLoading(true);
      setError(undefined);
      const { best, error: loadError } = await loadBestMetrics(connection);
      if (best) {
        const next = mostCompleteMetrics([metricsRef.current, best]) || best;
        metricsRef.current = next;
        setMetrics(next);
        writeCache(next);
        setLoading(false);
        if (best.trades < next.trades) {
          setError("Showing the last complete on-chain history");
        }
        return;
      }
      setLoading(false);
      setError(loadError instanceof Error ? loadError.message : "Unable to load activity");
    })();
    inFlight.current = task;
    void task.finally(() => {
      if (inFlight.current === task) inFlight.current = undefined;
    });
    return task;
  }, [connection]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { metrics, loading, error, refresh };
}
